import { useMemo, useState } from 'react';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CheckCircle2, Circle, GripVertical, Plus } from 'lucide-react';
import { t } from '../lib/i18n';
import { Avatar, LabelChip, PriorityDot, fmtDate } from './ui';

function nextOrder(prev, next) {
  if (prev == null && next == null) return 1000;
  if (prev == null) return next / 2;
  if (next == null) return prev + 1000;
  return (prev + next) / 2;
}

// ── grouped mode (group-by Status): table rows, no drag ───────────────────────
function Row({ task, roster, labels, done, onOpen, onToggleComplete, readOnly, isSelected, onToggleSelect }) {
  const overdue = task.due_date && !done && new Date(task.due_date) < new Date();
  const assignees = (task.assignee_mids || []).map((mid) => roster.find((m) => m.mid === mid)).filter(Boolean);
  const taskLabels = (task.labels || []).map((id) => labels.find((l) => l.id === id)).filter(Boolean);
  return (
    <tr
      onClick={() => onOpen(task.id)}
      className={`cursor-pointer border-b border-pm-border/60 hover:bg-pm-raised/60 ${done ? 'opacity-60' : ''}`}
    >
      {!readOnly && (
        <td className="w-7 py-2 pl-1">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(task.id)}
            onClick={(e) => e.stopPropagation()}
            className="accent-[var(--pm-accent)]"
            aria-label={`Select ${task.title}`}
          />
        </td>
      )}
      <td className="w-8 py-2 pl-2">
        {!readOnly && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleComplete(task); }}
            className={done ? 'text-pm-accent' : 'text-pm-faint hover:text-pm-accent'}
            title={done ? 'Reopen' : 'Complete'}
          >
            {done ? <CheckCircle2 size={15} /> : <Circle size={15} />}
          </button>
        )}
      </td>
      <td className={`py-2 pr-3 text-sm ${done ? 'line-through' : ''}`}>
        {task.title}
        {(task.subtasks || []).length > 0 && (
          <span className="pm-num ml-2 text-xs text-pm-faint">
            {(task.subtasks || []).filter((s) => s.done).length}/{(task.subtasks || []).length}
          </span>
        )}
      </td>
      <td className="hidden py-2 pr-3 sm:table-cell">
        <div className="flex flex-wrap gap-1">
          {taskLabels.map((l) => (<LabelChip key={l.id} label={l} small />))}
        </div>
      </td>
      <td className="hidden whitespace-nowrap py-2 pr-3 text-xs md:table-cell" style={overdue ? { color: 'var(--pm-danger)' } : { color: 'var(--pm-faint)' }}>
        {fmtDate(task.due_date)}
      </td>
      <td className="hidden py-2 pr-3 md:table-cell"><PriorityDot priority={task.priority} /></td>
      <td className="py-2 pr-2">
        <span className="flex justify-end -space-x-1.5">
          {assignees.slice(0, 3).map((m) => (<Avatar key={m.mid} name={m.name} size={20} />))}
        </span>
      </td>
    </tr>
  );
}

function GroupSection({ group, tasks, roster, labels, readOnly, onOpen, onCreate, onToggleComplete, selected, onToggleSelect }) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    await onCreate(group.id, title.trim());
    setTitle('');
  };
  return (
    <section className="mb-6">
      <div className="mb-1 flex items-center gap-2">
        <span className="h-2 w-2 rounded-full" style={{ background: group.color }} />
        <h2 className="text-sm font-semibold">{group.name}</h2>
        <span className="pm-num text-xs text-pm-faint">{tasks.length}</span>
        {!readOnly && (
          <button type="button" onClick={() => setAdding(true)} className="rounded p-1 text-pm-faint hover:bg-pm-raised hover:text-pm-text" title="Add task">
            <Plus size={13} />
          </button>
        )}
      </div>
      <table className="w-full table-auto border-collapse">
        <tbody>
          {tasks.map((task) => (
            <Row
              key={task.id}
              task={task}
              roster={roster}
              labels={labels}
              done={Boolean(task.completed_at) || Boolean(group.is_done_group)}
              readOnly={readOnly}
              onOpen={onOpen}
              onToggleComplete={onToggleComplete}
              isSelected={selected?.has(task.id) || false}
              onToggleSelect={onToggleSelect}
            />
          ))}
        </tbody>
      </table>
      {adding && (
        <form onSubmit={submit} className="mt-1.5">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => !title.trim() && setAdding(false)}
            onKeyDown={(e) => e.key === 'Escape' && setAdding(false)}
            placeholder="Task title, Enter to add"
            autoFocus
            className="w-full max-w-md rounded-md border border-pm-border bg-pm-input px-2 py-1.5 text-sm outline-none focus:border-[var(--pm-focus-border)]"
          />
        </form>
      )}
      {tasks.length === 0 && !adding && <p className="py-1 text-xs text-pm-faint">No tasks.</p>}
    </section>
  );
}

// ── flat mode (default): one linear, drag-sortable list ───────────────────────
function FlatRow({ task, roster, labels, done, readOnly, onOpen, onToggleComplete, isSelected, onToggleSelect }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id, disabled: readOnly,
  });
  const overdue = task.due_date && !done && new Date(task.due_date) < new Date();
  const assignees = (task.assignee_mids || []).map((mid) => roster.find((m) => m.mid === mid)).filter(Boolean);
  const taskLabels = (task.labels || []).map((id) => labels.find((l) => l.id === id)).filter(Boolean);
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}
      onClick={() => onOpen(task.id)}
      className={`flex cursor-pointer items-center gap-2 px-2 py-2 hover:bg-pm-raised/60 ${done ? 'opacity-60' : ''}`}
    >
      {!readOnly && (
        <button
          type="button"
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          className="cursor-grab touch-none text-pm-faint hover:text-pm-text active:cursor-grabbing"
          title="Drag to reorder"
        >
          <GripVertical size={14} />
        </button>
      )}
      {!readOnly && (
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(task.id)}
          onClick={(e) => e.stopPropagation()}
          className="accent-[var(--pm-accent)]"
          aria-label={`Select ${task.title}`}
        />
      )}
      {!readOnly && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleComplete(task); }}
          className={`shrink-0 ${done ? 'text-pm-accent' : 'text-pm-faint hover:text-pm-accent'}`}
          title={done ? 'Reopen' : 'Complete'}
        >
          {done ? <CheckCircle2 size={15} /> : <Circle size={15} />}
        </button>
      )}
      <span className={`min-w-0 flex-1 truncate text-sm ${done ? 'line-through' : ''}`}>
        {task.title}
        {(task.subtasks || []).length > 0 && (
          <span className="pm-num ml-2 text-xs text-pm-faint">
            {(task.subtasks || []).filter((s) => s.done).length}/{(task.subtasks || []).length}
          </span>
        )}
      </span>
      <span className="hidden gap-1 sm:flex">
        {taskLabels.map((l) => (<LabelChip key={l.id} label={l} small />))}
      </span>
      {task.due_date && (
        <span className="hidden whitespace-nowrap text-xs md:inline" style={overdue ? { color: 'var(--pm-danger)' } : { color: 'var(--pm-faint)' }}>
          {fmtDate(task.due_date)}
        </span>
      )}
      <PriorityDot priority={task.priority} />
      <span className="flex -space-x-1.5">
        {assignees.slice(0, 3).map((m) => (<Avatar key={m.mid} name={m.name} size={20} />))}
      </span>
    </div>
  );
}

function FlatList({ tasks, doneGroupIds, roster, labels, readOnly, onOpen, onToggleComplete, onReorder, selected, onToggleSelect }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const ordered = useMemo(() => [...tasks].sort((a, b) => a.order - b.order), [tasks]);

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = ordered.map((x) => x.id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    // build the post-move ordering and read the moved task's new neighbours
    const moved = ordered[from];
    const after = ordered.filter((x) => x.id !== moved.id);
    after.splice(to, 0, moved);
    const idx = after.findIndex((x) => x.id === moved.id);
    const order = nextOrder(after[idx - 1]?.order ?? null, after[idx + 1]?.order ?? null);
    onReorder(moved.id, order);
  };

  if (ordered.length === 0) {
    return <div className="rounded-lg border border-dashed border-pm-border py-16 text-center text-sm text-pm-muted">{t('No tasks.')}</div>;
  }
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ordered.map((x) => x.id)} strategy={verticalListSortingStrategy}>
        <div className="divide-y divide-pm-border/60 rounded-lg border border-pm-border bg-pm-surface shadow-[var(--pm-elev-1)]">
          {ordered.map((task) => (
            <FlatRow
              key={task.id}
              task={task}
              roster={roster}
              labels={labels}
              done={Boolean(task.completed_at) || doneGroupIds.has(task.group_id)}
              readOnly={readOnly}
              onOpen={onOpen}
              onToggleComplete={onToggleComplete}
              isSelected={selected?.has(task.id) || false}
              onToggleSelect={onToggleSelect}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export default function TaskListView({
  groups, tasks, roster, labels, readOnly, onOpen, onCreate, onToggleComplete,
  selected, onToggleSelect, groupBy = 'none', onReorder,
}) {
  const doneGroupIds = useMemo(
    () => new Set(groups.filter((g) => g.is_done_group).map((g) => g.id)),
    [groups],
  );

  if (groupBy === 'status') {
    return (
      <div className="h-full overflow-y-auto pr-1">
        {groups.map((g) => (
          <GroupSection
            key={g.id}
            group={g}
            tasks={tasks.filter((task) => task.group_id === g.id).sort((a, b) => a.order - b.order)}
            roster={roster}
            labels={labels}
            readOnly={readOnly}
            onOpen={onOpen}
            onCreate={onCreate}
            onToggleComplete={onToggleComplete}
            selected={selected}
            onToggleSelect={onToggleSelect}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto pr-1">
      <FlatList
        tasks={tasks}
        doneGroupIds={doneGroupIds}
        roster={roster}
        labels={labels}
        readOnly={readOnly}
        onOpen={onOpen}
        onToggleComplete={onToggleComplete}
        onReorder={onReorder}
        selected={selected}
        onToggleSelect={onToggleSelect}
      />
    </div>
  );
}
